/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { Box, Chip, IconButton, Popover, Typography } from '@mui/material';
import { useState } from 'react';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import StarIcon from '@mui/icons-material/Star';
import { useDispatch, useSelector } from 'react-redux';
import { selectAuthUser, selectOrganizationId } from 'modules/common/auth/selectors';
import { selectVisuals } from 'modules/dashboard/selectors';
import DescriptionRenderer from './description-renderer';
import { extractKeywordMeta } from '../functions';
import SearchStayDateRangePicker from './scenario-card-filters/search-stay-date-range-picker';
import SearchHotelDropdown from './scenario-card-filters/search-hotel-dropdown';
import SearchBookingPaceHorizonSelector from './scenario-card-filters/booking-pace-horizon-selector';
import SearchBookingDateRangePicker from './scenario-card-filters/search-booking-date-range-picker';
import SearchTrendBreakdownSelector from './scenario-card-filters/search-trend-breakdown-selector';
import SearchComparisonPeriodSelector from './scenario-card-filters/search-comparison-period-selector';
import SearchPercentageSelector from './scenario-card-filters/search-percentage-selector';
import SearchFocusedOn from './scenario-card-filters/search-focused-on';
import { CHIP_STYLE } from '../style';
import { searchActions } from '../slice';
import { selectFavoriteScenarios, selectSelectedScenario } from '../selectors';
import { SCENARIO_FILTERS } from '../constants';
import SearchComparisonAlignment from './scenario-card-filters/search-comparison-alignment';

/**
 * Returns the appropriate filter component based on the filter name.
 */
const getFilterComponent = (filterData, scenario, handleClose) => {
  const filterName = filterData?.filterName;
  //
  const foundFilter = scenario?.visuals?.find((visual) => visual.id === filterData?.visualId)
    ?.filters?.[filterData?.filterName];

  switch (filterName) {
    case SCENARIO_FILTERS.STAY_DATE:
      return <SearchStayDateRangePicker filterData={filterData} found={foundFilter} />;
    case SCENARIO_FILTERS.HOTEL:
    case SCENARIO_FILTERS.HOTEL_SET:
      return (
        <SearchHotelDropdown
          filterData={filterData}
          handleClose={handleClose}
          found={foundFilter}
        />
      );
    case SCENARIO_FILTERS.BOOKING_PACE_HORIZON:
      return (
        <SearchBookingPaceHorizonSelector
          filterData={filterData}
          handleClose={handleClose}
          found={foundFilter}
        />
      );
    case SCENARIO_FILTERS.BOOKING_DATE:
      return <SearchBookingDateRangePicker filterData={filterData} found={foundFilter} />;
    case SCENARIO_FILTERS.BREAKDOWN:
      return (
        <SearchTrendBreakdownSelector
          filterData={filterData}
          handleClose={handleClose}
          found={foundFilter}
        />
      );
    case SCENARIO_FILTERS.COMPARISON_PERIOD:
      return (
        <SearchComparisonPeriodSelector
          filterData={filterData}
          handleClose={handleClose}
          found={foundFilter}
        />
      );
    case SCENARIO_FILTERS.PERCENTAGE:
      return (
        <SearchPercentageSelector
          filterData={filterData}
          handleClose={handleClose}
          found={foundFilter}
        />
      );
    case SCENARIO_FILTERS.FOCUS_ON:
      return (
        <SearchFocusedOn filterData={filterData} handleClose={handleClose} found={foundFilter} />
      );
    case SCENARIO_FILTERS.COMPARISON_ALIGNMENT:
      return (
        <SearchComparisonAlignment
          filterData={filterData}
          handleClose={handleClose}
          found={foundFilter}
        />
      );
    default:
      return null;
  }
};

const ScenarioContent = ({ scenario }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [filterData, setFilterData] = useState(null);
  //
  const organizationId = useSelector(selectOrganizationId);
  const authUser = useSelector(selectAuthUser);
  const favoriteScenarios = useSelector(selectFavoriteScenarios);
  const selectedScenario = useSelector(selectSelectedScenario);
  const visualDetails = useSelector(selectVisuals);
  //
  const dispatch = useDispatch();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);
  const isFavorite = favoriteScenarios?.find((s) => s.id === scenario.id);
  //
  const handleClose = (event) => {
    event?.stopPropagation();
    setAnchorEl(null);
  };
  //
  const handleFavoriteScenario = (event) => {
    event?.stopPropagation();
    dispatch(
      searchActions.updateFavoriteScenario({
        organizationId,
        scenarioId: scenario?.id,
        userId: authUser?.id,
      })
    );
    //
    if (isFavorite) {
      const newFavoriteScenarios = favoriteScenarios.filter((s) => s.id !== scenario.id);
      dispatch(searchActions.setFavoriteScenario(newFavoriteScenarios));
    } else {
      const newFavoriteScenarios = [...favoriteScenarios, scenario];
      dispatch(searchActions.setFavoriteScenario(newFavoriteScenarios));
    }
  };
  return (
    <Box position="relative">
      {!selectedScenario ? (
        <Box sx={{ position: 'absolute', top: -16, right: -10 }}>
          <IconButton title="Click to pin to scenario" onClick={handleFavoriteScenario}>
            {isFavorite ? <StarIcon /> : <StarBorderIcon />}
          </IconButton>
        </Box>
      ) : null}
      <Typography fontWeight={600}>{scenario.name}</Typography>
      <DescriptionRenderer
        description={scenario.description}
        keywords={extractKeywordMeta(scenario.id, scenario.visuals || [])}
        onKeywordClick={(event, data) => {
          event.stopPropagation();
          setFilterData(data);
          handleClick(event);
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              bgcolor: 'transparent',
              boxShadow: 'none',
            },
          },
        }}
      >
        <Box overflow="auto">{getFilterComponent(filterData, scenario, handleClose)}</Box>
      </Popover>
      <Box sx={{ mt: 'auto', pt: 4, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {scenario?.visuals?.map((visual) => (
          <Chip
            key={visual.displayName}
            label={visualDetails?.find((v) => v?.pageDisplayName === visual?.displayName)?.name}
            sx={CHIP_STYLE}
          />
        ))}
      </Box>
    </Box>
  );
};

export default ScenarioContent;
